import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@routific/hive/v5';
import { DeliveryMethod, OrderCompletionStatus, VisitDtoModelType } from '../../../../store/mst/models';
import orderDetails from './OrderDetails';
import OrdersLocationMap from '../../OrderLocationsMap/index';
import { OrderAttemptLocation } from '../../OrderLocationsMap/mapHelperFunctions';
import { ContentScroll, MapContainer, ContentWrapper } from './styles';
import OrderTimelineSection from './OrderTimelineSection';
import CustomerDetailsSection from './CustomerDetailsSection';
import { DetailsCard } from '../../../shared/DetailsCard';
import OrderDetailsPanelSkeleton from './atoms/OrderDetailsPanelSkeleton';
import { filterRows } from './utils';
import OrderSourceTag from './atoms/OrderSourceTag';

export interface OrderDetailsOptions {
  canEditVisits: boolean;
  deliveryMethod?: DeliveryMethod;
}

interface Props {
  visit: VisitDtoModelType;
  orderDetailsOptions: OrderDetailsOptions;
  isLoading?: boolean;
  onOpenCustomerProfile?: (source: HTMLElement) => void;
}

const OrderDetailsContent: React.FC<Props> = ({ visit, orderDetailsOptions, isLoading = false, onOpenCustomerProfile }) => {
  const { t } = useTranslation('common');

  const activeLocation = visit.activeLocation;

  const orderAttemptLocations: OrderAttemptLocation[] = (visit.attempts ?? [])
    .flatMap((attempt) => {
      const location = attempt.completionLocation;
      if (!location) {
        return [];
      }
      return [{
        ...location,
        isMissed: attempt.status === OrderCompletionStatus.MISSED,
      }];
    });

  const hasValidLocation = activeLocation &&
    typeof activeLocation.latitude === 'number' &&
    typeof activeLocation.longitude === 'number' &&
    !isNaN(activeLocation.latitude) &&
    !isNaN(activeLocation.longitude);

  const showMap = Boolean(visit.hasLngLat && hasValidLocation);

  let mapSection = null;
  if (showMap) {
    mapSection = (
      <MapContainer data-cy="odp-map" data-testid="odp-map">
        <OrdersLocationMap
          locations={[activeLocation]}
          geolocation={undefined}
          selectedLocation={activeLocation}
          isDraggable={false}
          orderAttemptLocations={orderAttemptLocations}
          roundedCorners={false}
        />
      </MapContainer>
    );
  } else if (isLoading) {
    mapSection = (
      <MapContainer data-testid="odp-map-skeleton">
        <Skeleton width="100%" style={{ height: '100%', borderRadius: 0 }} />
      </MapContainer>
    );
  }

  const detailsSection = isLoading ? (
    <OrderDetailsPanelSkeleton />
  ) : (
    <ContentWrapper data-cy="order-details-content">
      <OrderTimelineSection visit={visit} />
      <CustomerDetailsSection visit={visit} orderDetailsOptions={orderDetailsOptions} onOpenCustomerProfile={onOpenCustomerProfile} />
      <DetailsCard
        data-testid="order-details-card"
        header={t('orderDetails.orderDetailsHeader')}
        headerAddon={<OrderSourceTag source={visit.orderSource} />}
        rows={filterRows(orderDetails(t, visit))}
      />
    </ContentWrapper>
  );

  return (
    <ContentScroll
      data-testid={isLoading ? 'order-details-panel-skeleton' : undefined}
      role={isLoading ? 'status' : undefined}
      aria-busy={isLoading || undefined}
      aria-label={isLoading ? t('loading.generic') : undefined}
    >
      {mapSection}
      {detailsSection}
    </ContentScroll>
  );
};

export default observer(OrderDetailsContent);
